import { graphql, PageProps } from "gatsby";
import React, { FC, ReactNode } from "react";
import {
  GetProductQuery,
  SanityArticleCardCollection,
  SanityHero,
  SanityLinkCollection
} from "../../types/graphql-types";
import { ExtendedPortableText } from "../components/ExtendedPortableText";
import { HeroProductProps } from "../components/HeroProduct";
import { ShortcutSection } from "../components/sections/ShortcutSection";
import { SanityNode } from "../config/build/models/node";
import { useUrlMap } from "../hooks/useUrlMap";
import urlFor from "../utilities/images/imageBuilder";
import { buildInternalUrl } from "../utilities/routes/urlBuilder";
import { ProductPage } from "./layouts/ProductPage";
import { SanitySiteLayout } from "./layouts/SanitySiteLayout";

const SanityProductTemplate: FC<PageProps<GetProductQuery>> = ({ data }) => {
  const urlMap = useUrlMap();

  if (!data.currentPage) {
    return null;
  }

  const { currentPage } = data;

  const heroProps = {} as HeroProductProps;
  const hero = currentPage.hero as SanityHero | undefined;

  if (hero) {
    heroProps.subHeader = currentPage.hero?.subHeading ?? "";
    heroProps.headline = currentPage.hero?.heading ?? "";
    heroProps.preamble = currentPage.hero?.text ?? "";

    if (hero.primaryLinkButton) {
      const primaryLinkNode = currentPage.hero?.primaryLinkButton?.linkReference as
        | SanityNode
        | undefined;

      heroProps.primaryBtn = {
        title:
          currentPage.hero?.primaryLinkButton?.linkTitle ??
          currentPage.hero?.primaryLinkButton?.linkText ??
          "",
        url: primaryLinkNode ? buildInternalUrl(primaryLinkNode, null, urlMap) : "",
        label: currentPage.hero?.primaryLinkButton?.linkText ?? ""
      };
    }
    if (hero.secondaryLinkButton) {
      const secondaryLinkNode = currentPage.hero?.secondaryLinkButton?.linkReference as
        | SanityNode
        | undefined;

      heroProps.secondaryBtn = {
        title:
          currentPage.hero?.secondaryLinkButton?.linkTitle ??
          currentPage.hero?.secondaryLinkButton?.linkText ??
          "",
        url: secondaryLinkNode ? buildInternalUrl(secondaryLinkNode, null, urlMap) : "",
        label: currentPage.hero?.secondaryLinkButton?.linkText ?? ""
      };
    }
    if (hero.image) {
      heroProps.imgUrl = urlFor(hero.image).height(1500).width(2000).url();
    }
  }

  const children: ReactNode[] = [];

  const shortcuts = currentPage.linkCollection as SanityLinkCollection | undefined;

  if (shortcuts?.links?.length) {
    children.push(
      <ShortcutSection
        body={shortcuts.text ?? ""}
        heading={shortcuts.heading ?? ""}
        subheading={shortcuts.subHeading ?? ""}
        shortcuts={
          shortcuts.links?.map(link => {
            const linkNode = link?.linkReference as SanityNode | undefined;

            return {
              link: {
                url: linkNode ? buildInternalUrl(linkNode, null, urlMap) : "",
                title: link?.linkText ?? ""
              },
              children: link?.linkText ?? ""
            };
          }) ?? []
        }
      />
    );
  }

  const highlightedCards = currentPage.highlightedCardCollection as
    | SanityArticleCardCollection
    | undefined;

  if (highlightedCards) {
    children.push(
      <ExtendedPortableText
        value={{
          ...highlightedCards,
          _type: "articleCardCollection"
        }}
        theme="prominent"
      />
    );
  }

  currentPage._rawBlocks?.map(value =>
    children.push(<ExtendedPortableText value={value} />)
  );

  return (
    <SanitySiteLayout>
      <ProductPage heroProduct={heroProps} children={children} />
    </SanitySiteLayout>
  );
};

export { Head } from "../components/Head";

export const query = graphql`
  query GetProduct($_id: String!) {
    currentPage: sanityProduct(_id: { eq: $_id }) {
      title
      _updatedAt
      language
      _id
      _rawBlocks(resolveReferences: { maxDepth: 10 })
      highlightedCardCollection {
        text
        subHeading
        heading
        cards {
          text
          image {
            ...SanityExtendedImageFragment
          }
          heading
          link {
            ...SanityInternalLinkFragment
          }
        }
      }
      linkCollection {
        text
        subHeading
        heading
        links {
          ...SanityInternalLinkFragment
        }
      }
      hero {
        heading
        subHeading
        text
        image {
          ...SanityExtendedImageFragment
        }
        primaryLinkButton {
          ...SanityInternalLinkFragment
        }
        secondaryLinkButton {
          ...SanityInternalLinkFragment
        }
      }
    }
  }
`;

export default SanityProductTemplate;
